import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import { QRCodeSVG } from "qrcode.react";
import { FaCopy, FaCheck } from "react-icons/fa";
import { IoShareSocial } from "react-icons/io5";
import StepWrapper from "./StepWrapper";

interface CreatedFlatData {
  flat_id: number;
  room_id: number;
  join_code: string;
}

interface SuccessStepProps {
  createdFlat: CreatedFlatData;
  onViewFlat: () => void;
}

const JOIN_URL = "https://flateroo.com/app/joinflat/";

const SuccessStep: React.FC<SuccessStepProps> = ({
  createdFlat,
  onViewFlat,
}) => {
  const { t } = useTranslation();
  const colors = useColors();
  const [copied, setCopied] = useState(false);

  const joinLink = `${JOIN_URL}${createdFlat.join_code}/${createdFlat.flat_id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = async () => {
    const shareMessage =
      t("flats_tab.add_flat.share_to_join_message") ||
      "Join my flat on Flateroo!";

    if (navigator.share) {
      try {
        await navigator.share({
          title: t("share_flat") || "Share Flat",
          text: `${shareMessage}\n\n${joinLink}`,
        });
      } catch (err) {
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  return (
    <StepWrapper
      title={t("flats_tab.add_flat.flat_qr_code") || "Add Flat Members"}
      subtitle={
        t("flat_created_success") ||
        "Your flat has been created successfully!"
      }
    >
      <div className="flex flex-col items-center justify-center py-4">
        {/* QR Code */}
        <div
          className="p-7 rounded-3xl mb-6"
          style={{
            background: `linear-gradient(135deg, rgba(91, 61, 246, 0.05) 0%, rgba(2, 2, 16, 0.02) 100%)`,
          }}
        >
          <QRCodeSVG
            value={createdFlat.join_code}
            size={170}
            level="M"
            includeMargin={false}
            fgColor={colors.textNeutralDefault}
            bgColor="transparent"
          />
        </div>

        <p
          className="text-sm mb-7 text-center max-w-xs leading-relaxed"
          style={{ color: colors.textNeutralMuted }}
        >
          {t("flats_tab.add_flat.qr_code_hint") ||
            "Share this QR code or link with your flatmates so they can join your flat."}
        </p>

        {/* Action buttons */}
        <div className="flex flex-wrap justify-center gap-3 w-full max-w-sm">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2.5 px-6 py-3.5 rounded-full font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              backgroundColor: "rgba(2, 2, 16, 0.06)",
              color: colors.textNeutralDefault,
            }}
          >
            {copied ? <FaCheck size={14} /> : <FaCopy size={14} />}
            <span>
              {copied
                ? t("copied") || "Copied!"
                : t("copy_link") || "Copy Link"}
            </span>
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-2.5 px-6 py-3.5 rounded-full font-gnarly-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
          >
            <IoShareSocial size={16} />
            <span>
              {t("flats_tab.add_flat.share_flat_link") || "Share Link"}
            </span>
          </button>
        </div>

      </div>
    </StepWrapper>
  );
};

export default SuccessStep;
